import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { Building2, Plus, X, Save, Pause, Play, Trash2, Copy, Mail, RefreshCw, LogIn, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { fmtRelative } from '@/lib/format';

export default function SuperTenantsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [created, setCreated] = useState(null);
  const [health, setHealth] = useState([]);
  const load = async () => { const { data } = await api.get('/superadmin/tenants'); setItems(data.tenants); };
  useEffect(() => {
    load();
    api.get('/superadmin/health').then((r) => setHealth(r.data.tenants || [])).catch(() => {});
  }, []);

  const impersonate = async (t) => {
    if (!confirm(`¿Entrar como admin de "${t.name}"?`)) return;
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.id}/impersonate`);
      localStorage.setItem('zunoid_access', data.access_token);
      localStorage.setItem('zunoid_refresh', data.refresh_token);
      toast.success('Sesión impersonada', { description: data.user?.email });
      window.location.href = '/app/dashboard';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo impersonar');
    }
  };
  const newTenant = () => setEditing({ name: '', slug: '', admin_email: '', admin_name: '', contact_phone: '' });
  const create = async () => {
    try {
      const { data } = await api.post('/superadmin/tenants', editing);
      setCreated({ tenant: data.tenant, invitation: data.invitation });
      setEditing(null);
      load();
      if (data.invitation.email_sent) toast.success('Tenant creado y invitación enviada por email');
      else toast.success('Tenant creado', { description: 'Comparte el enlace de invitación manualmente — el email no pudo enviarse.' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const toggle = async (t) => {
    await api.patch(`/superadmin/tenants/${t.id}`, { status: t.status === 'active' ? 'suspended' : 'active' });
    load(); toast.success(t.status === 'active' ? 'Suspendido' : 'Reactivado');
  };
  const del = async (t) => {
    if (!confirm(`¿Eliminar permanentemente "${t.name}"?\n\nEsto borrará TODOS sus datos (usuarios, leads, mensajes, plantillas, etc.) de forma irreversible.`)) return;
    if (!confirm(`Confirma una vez más: borrar TODO de "${t.name}"`)) return;
    try {
      const { data } = await api.delete(`/superadmin/tenants/${t.id}`);
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success('Tenant eliminado', { description: `${total} registros borrados` });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const resend = async (t) => {
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.id}/resend-invite`);
      if (data.sent) toast.success('Invitación reenviada por email');
      else { navigator.clipboard.writeText(data.invite_link); toast.info('Enlace de invitación copiado', { description: 'El email no pudo enviarse, comparte manualmente.' }); }
    } catch (err) { toast.error(err?.response?.data?.detail || 'No hay invitación pendiente'); }
  };
  const copyLink = (link) => { navigator.clipboard.writeText(link); toast.success('Enlace copiado'); };

  return (
    <div>
      <PageHeader title="Tenants" description="Empresas registradas en la plataforma." actions={<button data-testid="tenants-new" onClick={newTenant} className="btn-primary"><Plus className="h-4 w-4" /> Nuevo tenant</button>} />
      {items.length === 0 ? (
        <EmptyState icon={Building2} title="Sin tenants" description="Crea tu primer tenant para empezar." cta={<button onClick={newTenant} className="btn-primary">Crear</button>} />
      ) : (
        <div className="surface">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="px-3 py-2">Empresa</th><th className="px-3 py-2">Slug</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2 text-right">Usuarios</th><th className="px-3 py-2 text-right">Leads</th><th className="px-3 py-2">Creada</th><th></th></tr></thead>
            <tbody>{items.map((t) => (
              <tr key={t.id} data-testid="tenant-row" className="h-10 border-b border-border/70 hover:bg-accent/60">
                <td className="px-3 font-medium">{t.name}</td>
                <td className="px-3 font-mono text-xs text-muted-foreground">{t.slug}</td>
                <td className="px-3"><span className={`chip ${t.status === 'active' ? 'border-[hsl(var(--success))]/40 text-[hsl(var(--success))]' : 'border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))]'}`}>{t.status}</span></td>
                <td className="px-3 text-right tabular-nums">{t.users_count}</td>
                <td className="px-3 text-right tabular-nums">{t.leads_count}</td>
                <td className="px-3 text-xs text-muted-foreground">{fmtRelative(t.created_at)}</td>
                <td className="px-3 text-right whitespace-nowrap">
                  {t.users_count === 0 && (
                    <button data-testid="tenant-resend" onClick={() => resend(t)} className="btn-ghost h-7 px-2 text-xs"><Mail className="h-3 w-3" /> Reenviar invitación</button>
                  )}
                  <button onClick={() => impersonate(t)} className="btn-ghost h-7 px-2 text-xs" title="Entrar como admin del tenant">
                    <LogIn className="h-3 w-3" /> Entrar
                  </button>
                  <button onClick={() => toggle(t)} className="btn-ghost h-7 px-2 text-xs">{t.status === 'active' ? <><Pause className="h-3 w-3" /> Suspender</> : <><Play className="h-3 w-3" /> Activar</>}</button>
                  <button data-testid="tenant-delete" onClick={() => del(t)} className="btn-ghost h-7 w-7 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {health.length > 0 && (
        <div className="surface p-4 mt-4">
          <h2 className="font-display font-semibold mb-2 flex items-center gap-2"><Activity className="h-4 w-4" /> Health por tenant</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1 pr-2">Tenant</th>
                  <th className="py-1 pr-2">WA</th>
                  <th className="py-1 pr-2">SMTP</th>
                  <th className="py-1 pr-2">Users</th>
                  <th className="py-1 pr-2">Leads</th>
                  <th className="py-1">Último msg</th>
                </tr>
              </thead>
              <tbody>
                {health.map((h) => (
                  <tr key={h.id} className="border-b border-border/40">
                    <td className="py-1 pr-2 font-medium">{h.name}</td>
                    <td className="py-1 pr-2">{h.wa_mode}{h.wa_qr_status ? ` (${h.wa_qr_status})` : ''}</td>
                    <td className="py-1 pr-2">{h.smtp_configured ? '✓' : '—'}</td>
                    <td className="py-1 pr-2 tabular-nums">{h.users}</td>
                    <td className="py-1 pr-2 tabular-nums">{h.leads}</td>
                    <td className="py-1 text-muted-foreground">{h.last_message_at ? fmtRelative(h.last_message_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Nuevo tenant</h2><button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <p className="text-xs text-muted-foreground mb-4">Se enviará una invitación por email al admin para que defina su propia contraseña. Si no hay SMTP global configurado, el enlace de invitación se mostrará aquí.</p>
            <div className="space-y-3">
              <label>Nombre de la empresa<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input mt-1" /></label>
              <label>Slug (opcional)<input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="input mt-1 font-mono" /></label>
              <div className="border-t border-border pt-3 text-xs text-muted-foreground">Admin inicial</div>
              <label>Nombre del admin<input value={editing.admin_name} onChange={(e) => setEditing({ ...editing, admin_name: e.target.value })} className="input mt-1" /></label>
              <label>Email del admin<input type="email" value={editing.admin_email} onChange={(e) => setEditing({ ...editing, admin_email: e.target.value })} className="input mt-1" /></label>
            </div>
            <div className="flex justify-end gap-2 mt-4"><button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button><button data-testid="tenant-create" onClick={create} className="btn-primary"><Save className="h-4 w-4" /> Crear y enviar invitación</button></div>
          </div>
        </div>
      )}
      {created && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setCreated(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Tenant creado</h2><button onClick={() => setCreated(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <p className="text-sm">Empresa <b>{created.tenant.name}</b> creada exitosamente.</p>
            <div className="mt-3 surface p-3 border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5">
              <div className="text-xs font-medium mb-1">Estado del email</div>
              {created.invitation.email_sent ? (
                <div className="text-sm text-[hsl(var(--success))]">✓ Email de invitación enviado</div>
              ) : (
                <div className="text-sm text-[hsl(var(--warning))]">⚠ No se pudo enviar el email ({created.invitation.email_reason || 'SMTP no configurado'}). Comparte el enlace manualmente:</div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 font-mono text-xs break-all bg-secondary/40 p-2 rounded-[8px]">{created.invitation.invite_link}</code>
                <button onClick={() => copyLink(created.invitation.invite_link)} className="btn-ghost h-8 w-8 p-0"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
            <button onClick={() => setCreated(null)} className="btn-primary w-full mt-4">Listo</button>
          </div>
        </div>
      )}
    </div>
  );
}
